import api from './api';

import type {
  Notification,
  NotificationTemplate,
  NotificationChannel,
  SupportTicket,
  TicketFilters,
  PaginatedResponse,
} from '@/types';

/** Board #234: a Support Category configured in the system. These are what the
 *  ticket create form offers — a broker's own categories plus the
 *  platform-wide ones. Free-text categories are gone. */
export interface SupportTicketCategory {
  id: string;
  name: string;
  code: string;
  description: string;
  display_order: number;
  is_active: boolean;
  broker: string | null;
  is_platform_wide: boolean;
  created_at: string;
  updated_at: string;
}

/** Board #234: the fields the create form posts. `support_category` and
 *  `customer_record` are ids the server re-checks against the caller's broker,
 *  so an id from another tenant is a 400, not a silent write. */
/** Board #262: what the Support Categories management screen writes. `broker`
 *  is absent on purpose — the server stamps the caller's own broker, so a
 *  broker admin cannot publish a category to every tenant. */
export interface TicketCategoryPayload {
  name: string;
  code: string;
  description?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface SupportTicketCreatePayload {
  subject: string;
  description: string;
  priority: string;
  support_category?: string;
  customer_record?: string;
}


/** Board #231: a row of `communications.InAppNotification` -- the table the
 *  Notifications page actually lists.
 *
 *  It is NOT the `Notification` type in `@/types`, which describes a
 *  `Communication` (an outbound email/SMS: recipient_name, channel_name,
 *  status, sent_at). The page rendered those five fields over these rows, so
 *  every cell but the message was permanently blank. An in-app notification
 *  has no channel and no delivery status -- it is addressed to the reader and
 *  is either read or not.
 *
 *  `event` and `event_label` are derived server-side from `action_data`;
 *  `InAppNotification` has no event column and adding one would need a
 *  migration. */
export interface InAppNotification {
  id: string;
  title: string;
  message: string;
  icon: string;
  image_url: string;
  action_url: string;
  action_text: string;
  action_data: Record<string, unknown>;
  event: string;
  event_label: string;
  related_object_type: string;
  notification_type: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  is_read: boolean;
  read_at: string | null;
  is_dismissed: boolean;
  expires_at: string | null;
  created_at: string;
}

export interface InAppNotificationFilters {
  search?: string;
  /** '' = any, 'true' = read only, 'false' = unread only. */
  is_read?: string;
  event?: string;
  priority?: string;
  date_from?: string;
  date_to?: string;
}

export interface NotificationEventOption {
  value: string;
  label: string;
}

/* ------------------------------------------------------------------------ *
 * Communication gateways and channel-aware templates (boards #233 / #232)
 *
 * These types describe what the API actually returns. The ones in `@/types`
 * (`NotificationChannel`, `NotificationTemplate`, `ChatMessage`) describe a
 * different, imagined API: a template there has `channel` (a uuid),
 * `channel_name`, `body` and `is_active`, while the server has `channel_type`
 * (a string like 'email'), `content` and a six-state `status`. TemplatesPage
 * was written against those, which is why it threw on `variables.length` and
 * why its create modal always 400ed.
 * ------------------------------------------------------------------------ */

export type CommunicationChannelType = 'email' | 'sms' | 'whatsapp' | 'push' | 'voice' | 'in_app';

/** A gateway row as it appears in the Settings list. Note there is no
 *  `config`: it holds provider API keys and is write-only on the server. */
export interface GatewayListRow {
  id: string;
  name: string;
  channel_type: CommunicationChannelType;
  channel_type_display: string;
  provider: string;
  provider_display: string;
  sender_id: string;
  sender_name: string;
  is_active: boolean;
  is_default: boolean;
  /** Every required credential is present. NOT "this gateway can send". */
  is_configured: boolean;
  /** Named credentials still missing, so the UI can say what to fill in. */
  missing_config: string[];
  /** Whether any code exists that can hand a message to this provider.
   *  `false` for every provider today -- see `gateways.DELIVERY_IMPLEMENTED`
   *  on the server. */
  delivery_implemented: boolean;
  priority: number;
  created_at: string;
}

export interface Gateway extends GatewayListRow {
  broker: string | null;
  broker_name: string | null;
  /** The non-credential settings only. */
  config_public: Record<string, unknown>;
  /** NAMES of the credentials that hold a value. Never their values. */
  secrets_set: string[];
  required_config: string[];
  secret_config: string[];
  webhook_url: string;
  webhook_secret_set: boolean;
  daily_limit: number | null;
  hourly_limit: number | null;
  rate_limit_per_second: number | null;
  messages_sent_today: number;
  messages_sent_this_hour: number;
  updated_at: string;
}

export interface GatewayInput {
  name: string;
  channel_type: string;
  provider: string;
  /** Merged into the stored config server-side. Omit a key to keep it; send
   *  `null` to clear it. */
  config?: Record<string, string | null>;
  sender_id?: string;
  sender_name?: string;
  is_active?: boolean;
  daily_limit?: number | null;
  hourly_limit?: number | null;
}

export interface ProviderOption {
  value: string;
  label: string;
  required_config: string[];
  optional_config: string[];
  secret_config: string[];
  delivery_implemented: boolean;
}

export interface ProviderCatalogueRow {
  channel_type: CommunicationChannelType;
  channel_type_display: string;
  providers: ProviderOption[];
}

interface GatewayTestOutcome {
  status?: string;
  communication_id?: string;
  message_id?: string;
  error?: string | { message?: string };
  missing_config?: string[];
}

export interface GatewayTestResult {
  /** True only if the server returned 2xx. Nothing here is ever synthesised. */
  ok: boolean;
  status?: string;
  communication_id?: string;
  message_id?: string;
  error?: string;
  missing_config?: string[];
}

export type TemplateStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'active'
  | 'inactive';

export interface CommunicationTemplateRow {
  id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  category_display: string;
  channel_type: CommunicationChannelType;
  channel_type_display: string;
  status: TemplateStatus;
  status_display: string;
  is_active: boolean;
  subject: string;
  content_preview: string;
  whatsapp_template_name: string;
  dlt_template_id: string;
  variables: string[];
  version: number;
  times_used: number;
  created_at: string;
}

export interface CommunicationTemplate extends Omit<CommunicationTemplateRow, 'content_preview'> {
  content: string;
  html_content: string;
  whatsapp_template_namespace: string;
  header_type: string;
  header_content: string;
  footer_content: string;
  buttons: unknown[];
  sample_data: Record<string, unknown>;
  dlt_entity_id: string;
  broker: string | null;
  updated_at: string;
}

export interface CommunicationTemplateInput {
  name?: string;
  code?: string;
  description?: string;
  category?: string;
  channel_type?: string;
  subject?: string | null;
  content?: string;
  html_content?: string;
  whatsapp_template_name?: string;
  whatsapp_template_namespace?: string;
  header_type?: string;
  header_content?: string;
  footer_content?: string;
  dlt_template_id?: string;
  dlt_entity_id?: string;
  variables?: string[];
}

export interface CommunicationTemplateFilters {
  search?: string;
  channel_type?: string;
  category?: string;
  status?: string;
}

export interface TemplateChannelSchema {
  channel_type: CommunicationChannelType;
  channel_type_display: string;
  required_fields: string[];
  optional_fields: string[];
  unsupported_fields: string[];
  field_labels: Record<string, string>;
}

export interface MessageThread {
  recipient: string;
  recipient_name: string;
  channel_type: string;
  last_message_at: string;
  last_direction: 'inbound' | 'outbound';
  last_status: string;
  last_preview: string;
  last_error: string;
  message_count: number;
  inbound_count: number;
  outbound_count: number;
  failed_count: number;
}

export interface MessageThreadsResponse {
  channel_type: string;
  threads: MessageThread[];
  /** Exact count from the database, not `threads.length`. */
  total_threads: number;
  /** True when more threads exist than this page listed. */
  truncated: boolean;
  /** False. Nothing on this platform creates an inbound Communication, so a
   *  tab can only ever show what was sent outward. The empty state says so. */
  inbound_supported: boolean;
  inbound_note: string;
}

export interface CommunicationRow {
  id: string;
  message_id: string;
  channel_type: string;
  direction: 'inbound' | 'outbound';
  recipient: string;
  recipient_name: string;
  subject: string;
  status: string;
  priority: string;
  sent_at: string | null;
  created_at: string;
  /** The provider's own words when the send failed. Rendered verbatim -- a
   *  paraphrase of "535 Authentication Credentials Invalid" helps nobody. */
  error_message?: string;
  content?: string;
}

/** What `POST /communications/messages/send/` returns.
 *
 *  A 201 means THE ATTEMPT IS RECORDED, not that the mail arrived. `status`
 *  is the only thing that says whether it went, and the compose screen must
 *  render it rather than assume a green tick. */
export interface SendEmailResult extends CommunicationRow {
  error_message?: string;
}

export interface SendEmailInput {
  /** One of these two is required by the server. */
  recipient?: string;
  customer_id?: string;
  subject: string;
  content: string;
  html_content?: string;
}

export interface ChatRoomRow {
  id: string;
  name: string;
  room_type: string;
  participant_count: number;
  last_message_at: string | null;
  last_message_preview: { content: string; sender: string; created_at: string } | null;
  created_at: string;
}

export interface ChatRoomDetail extends Omit<ChatRoomRow, 'participant_count' | 'last_message_preview'> {
  description: string;
  is_active: boolean;
}

export interface ChatMessageRow {
  id: string;
  room: string;
  sender: string | null;
  sender_name: string;
  message_type: string;
  content: string;
  attachments: unknown[];
  metadata: Record<string, unknown>;
  is_edited: boolean;
  is_deleted: boolean;
  is_own: boolean;
  created_at: string;
}

export const communicationService = {
  // In-app notifications (board #231)
  async getNotifications(
    page: number = 1,
    filters?: InAppNotificationFilters
  ): Promise<PaginatedResponse<InAppNotification>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.is_read) params.append('is_read', filters.is_read);
    if (filters?.event) params.append('event', filters.event);
    if (filters?.priority) params.append('priority', filters.priority);
    // These two have always been sent. Until the ViewSet grew a filter
    // backend nothing on the server read them.
    if (filters?.date_from) params.append('created_at_after', filters.date_from);
    if (filters?.date_to) params.append('created_at_before', filters.date_to);

    const response = await api.get<PaginatedResponse<InAppNotification>>(
      `/communications/notifications/?${params.toString()}`
    );
    return response.data;
  },

  async getNotification(id: string): Promise<InAppNotification> {
    const response = await api.get<InAppNotification>(`/communications/notifications/${id}/`);
    return response.data;
  },

  /** PATCH, not POST. The old call POSTed to `/notifications/{id}/mark_read/`,
   *  which is not a route at all: `mark_read` is a list-level action, and POST
   *  was blocked on the whole ViewSet anyway. */
  async setRead(id: string, isRead: boolean): Promise<InAppNotification> {
    const response = await api.patch<InAppNotification>(`/communications/notifications/${id}/`, {
      is_read: isRead,
    });
    return response.data;
  },

  async markAsRead(id: string): Promise<InAppNotification> {
    return this.setRead(id, true);
  },

  async markAllAsRead(): Promise<{ marked: number }> {
    const response = await api.post<{ marked: number }>('/communications/notifications/mark_read/', {
      mark_all: true,
    });
    return response.data;
  },

  async getUnreadCount(): Promise<number> {
    const response = await api.get<{ count: number }>('/communications/notifications/unread_count/');
    return response.data.count;
  },

  async getNotificationEvents(): Promise<NotificationEventOption[]> {
    const response = await api.get<NotificationEventOption[]>('/communications/notifications/events/');
    return response.data;
  },

  /** Composes an OUTBOUND message (email/SMS/WhatsApp) -- NOT an in-app
   *  notification.
   *
   *  This used to POST to `/communications/notifications/`, which is
   *  `InAppNotificationViewSet` -- a hard 405, and rightly so: in-app rows are
   *  raised by the event producers in `apps/communications/notifications.py`,
   *  and letting a client post one would let any signed-in user write into a
   *  notification feed. Repointed at the endpoint that really does send.
   *
   *  Note it will fail loudly with `DeliveryNotConfigured` until a
   *  `CommunicationChannel` exists -- no provider is configured on this
   *  platform yet (queue #232/#233). That is the honest answer; the 405 was
   *  not. */
  async sendNotification(data: {
    recipient: string;
    channel: string;
    template?: string;
    subject?: string;
    message: string;
  }): Promise<Notification> {
    const response = await api.post<Notification>('/communications/messages/send/', {
      channel_type: data.channel,
      recipient: data.recipient,
      template_code: data.template,
      subject: data.subject,
      content: data.message,
    });
    return response.data;
  },

  // Templates (board #233)
  //
  // `channel` and `is_active` are NOT query params the templates ViewSet
  // understands -- it filters on `channel_type`, `category` and `status`
  // (views.py: `filterset_fields`). Both were silently ignored, so the channel
  // dropdown on TemplatesPage filtered nothing at all.
  async getTemplates(
    page: number = 1,
    filters?: CommunicationTemplateFilters
  ): Promise<PaginatedResponse<CommunicationTemplateRow>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.channel_type) params.append('channel_type', filters.channel_type);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);

    const response = await api.get<PaginatedResponse<CommunicationTemplateRow>>(
      `/communications/templates/?${params.toString()}`
    );
    return response.data;
  },

  async getTemplate(id: string): Promise<CommunicationTemplate> {
    const response = await api.get<CommunicationTemplate>(`/communications/templates/${id}/`);
    return response.data;
  },

  /** The API shape the templates ViewSet really accepts: `channel_type` (a
   *  string like 'email'), `content`, and the per-channel extras. The create
   *  modal used to post `{channel: <channel uuid>, body}` -- neither is a
   *  field on `CommunicationTemplate`, so every create was a 400. */
  async createCommunicationTemplate(data: CommunicationTemplateInput): Promise<CommunicationTemplate> {
    const response = await api.post<CommunicationTemplate>('/communications/templates/', data);
    return response.data;
  },

  async updateCommunicationTemplate(
    id: string,
    data: CommunicationTemplateInput
  ): Promise<CommunicationTemplate> {
    const response = await api.patch<CommunicationTemplate>(`/communications/templates/${id}/`, data);
    return response.data;
  },

  /** Which fields each channel's template needs, straight from the server, so
   *  the create form and the validator cannot drift apart. */
  async getTemplateSchema(): Promise<TemplateChannelSchema[]> {
    const response = await api.get<TemplateChannelSchema[]>('/communications/templates/schema/');
    return response.data;
  },

  /** @deprecated Posts whatever it is given. Kept only because
   *  `hooks/useCommunications.ts` still references it; use
   *  `createCommunicationTemplate`, which is typed to the real API shape. */
  async createTemplate(
    data: Partial<NotificationTemplate> | CommunicationTemplateInput
  ): Promise<CommunicationTemplate> {
    const response = await api.post<CommunicationTemplate>('/communications/templates/', data);
    return response.data;
  },

  /** @deprecated See `createTemplate`; use `updateCommunicationTemplate`. */
  async updateTemplate(
    id: string,
    data: Partial<NotificationTemplate> | CommunicationTemplateInput
  ): Promise<CommunicationTemplate> {
    const response = await api.patch<CommunicationTemplate>(`/communications/templates/${id}/`, data);
    return response.data;
  },

  // Channels / communication gateways (boards #233 and #232)
  //
  // Only this GET existed, which is why production has zero
  // `CommunicationChannel` rows: nothing could ever create one.
  async getChannels(): Promise<NotificationChannel[]> {
    const response = await api.get<PaginatedResponse<NotificationChannel>>('/communications/channels/');
    return response.data.results;
  },

  async listGateways(): Promise<GatewayListRow[]> {
    const response = await api.get<PaginatedResponse<GatewayListRow>>('/communications/channels/?page_size=100');
    return response.data.results;
  },

  async getGateway(id: string): Promise<Gateway> {
    const response = await api.get<Gateway>(`/communications/channels/${id}/`);
    return response.data;
  },

  async createGateway(data: GatewayInput): Promise<Gateway> {
    const response = await api.post<Gateway>('/communications/channels/', data);
    return response.data;
  },

  /** `config` is merged server-side: a key you do not send keeps its stored
   *  value, and a key sent as `null` is cleared. That is what makes it possible
   *  to edit a gateway without the browser ever holding its secrets. */
  async updateGateway(id: string, data: Partial<GatewayInput>): Promise<Gateway> {
    const response = await api.patch<Gateway>(`/communications/channels/${id}/`, data);
    return response.data;
  },

  async deleteGateway(id: string): Promise<void> {
    await api.delete(`/communications/channels/${id}/`);
  },

  async getProviderCatalogue(): Promise<ProviderCatalogueRow[]> {
    const response = await api.get<ProviderCatalogueRow[]>('/communications/channels/providers/');
    return response.data;
  },

  /** Sends a real message through this gateway and reports what happened.
   *
   *  Resolves for both outcomes rather than throwing, because "it failed, and
   *  here is the provider's reason" is the answer the admin asked for. It
   *  never reports success on its own: `ok` is true only when the server
   *  returned 2xx, which today it cannot, because no provider adapter exists.
   */
  async testGateway(id: string, recipient?: string): Promise<GatewayTestResult> {
    try {
      const response = await api.post<{
        status?: string;
        communication_id?: string;
        message_id?: string;
      }>(
        `/communications/channels/${id}/test/`,
        // Omitted entirely rather than sent blank: the server then falls back
        // to the caller's own address, which is what "send me a test" means.
        recipient && recipient.trim() ? { recipient: recipient.trim() } : {}
      );
      return { ok: true, ...response.data };
    } catch (error) {
      const body = (error as { response?: { data?: unknown } })?.response?.data;
      if (body && typeof body === 'object') {
        const outcome = body as GatewayTestOutcome;
        // The project's exception envelope ({success, error:{message}}) for
        // raised errors; a plain body for the 503 the view returns itself.
        const message =
          typeof outcome.error === 'string'
            ? outcome.error
            : outcome.error?.message ?? 'The gateway test failed.';
        return {
          ok: false,
          status: outcome.status,
          communication_id: outcome.communication_id,
          message_id: outcome.message_id,
          missing_config: outcome.missing_config,
          error: message,
        };
      }
      return { ok: false, status: 'unknown', error: 'The gateway test failed and returned no detail.' };
    }
  },

  /** Compose and send one email (#114).
   *
   *  Resolves with the delivery record. It does NOT throw on a delivery
   *  failure -- the server answers 201 with `status: "failed"` and the
   *  provider's reason in `error_message`, and the caller must read it. It
   *  throws only when the request itself was refused (a customer with no
   *  address, a customer outside the caller's broker). */
  async sendEmail(data: SendEmailInput): Promise<SendEmailResult> {
    const response = await api.post<SendEmailResult>('/communications/messages/send/', {
      channel_type: 'email',
      recipient: data.recipient,
      customer_id: data.customer_id,
      subject: data.subject,
      content: data.content,
      html_content: data.html_content,
    });
    return response.data;
  },

  /** Outbound message threads for one channel type, grouped by counterparty
   *  (board #232's Chat tabs). Carries `inbound_supported`, which is false --
   *  see the empty state on ChatPage. */
  async getMessageThreads(channelType: string): Promise<MessageThreadsResponse> {
    const response = await api.get<MessageThreadsResponse>(
      `/communications/messages/threads/?channel_type=${encodeURIComponent(channelType)}`
    );
    return response.data;
  },

  async getMessagesForRecipient(
    channelType: string,
    recipient: string
  ): Promise<PaginatedResponse<CommunicationRow>> {
    const params = new URLSearchParams({ channel_type: channelType, search: recipient, page_size: '100' });
    const response = await api.get<PaginatedResponse<CommunicationRow>>(
      `/communications/messages/?${params.toString()}`
    );
    return response.data;
  },

  // Support Tickets
  async getTickets(page: number = 1, filters?: TicketFilters): Promise<PaginatedResponse<SupportTicket>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.category) params.append('category', filters.category);
    // Board #234: the filter bar now offers the configured Support Categories,
    // which filter on the FK, not on the legacy code.
    if (filters?.support_category) params.append('support_category', filters.support_category);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);

    const response = await api.get<PaginatedResponse<SupportTicket>>(`/communications/tickets/?${params.toString()}`);
    return response.data;
  },

  /** Board #234: the configured Support Categories for the create form. */
  async getTicketCategories(): Promise<SupportTicketCategory[]> {
    const response = await api.get<PaginatedResponse<SupportTicketCategory> | SupportTicketCategory[]>(
      '/communications/ticket-categories/?page_size=200',
    );
    return Array.isArray(response.data) ? response.data : response.data.results;
  },

  // Board #262 -- the Support Categories management screen.
  //
  // The picker above deliberately asks for live rows only. The management
  // screen must ask for the retired ones too: without `include_inactive` a
  // deactivated category disappears from the only screen that could bring it
  // back, which would make retiring one irreversible through the UI.
  async getTicketCategoriesForSettings(): Promise<SupportTicketCategory[]> {
    const response = await api.get<PaginatedResponse<SupportTicketCategory> | SupportTicketCategory[]>(
      '/communications/ticket-categories/?include_inactive=true&page_size=200',
    );
    return Array.isArray(response.data) ? response.data : response.data.results;
  },

  async createTicketCategory(data: TicketCategoryPayload): Promise<SupportTicketCategory> {
    const response = await api.post<SupportTicketCategory>('/communications/ticket-categories/', data);
    return response.data;
  },

  async updateTicketCategory(
    id: string,
    data: Partial<TicketCategoryPayload>,
  ): Promise<SupportTicketCategory> {
    const response = await api.patch<SupportTicketCategory>(
      `/communications/ticket-categories/${id}/`,
      data,
    );
    return response.data;
  },

  /** Retire a category. The server deactivates rather than deletes, so every
   *  ticket already filed under it stays readable. */
  async deactivateTicketCategory(id: string): Promise<void> {
    await api.delete(`/communications/ticket-categories/${id}/`);
  },

  /** Bring a retired category back. There is no separate endpoint: is_active
   *  is an ordinary writable field on a row the caller owns. */
  async activateTicketCategory(id: string): Promise<SupportTicketCategory> {
    const response = await api.patch<SupportTicketCategory>(
      `/communications/ticket-categories/${id}/`,
      { is_active: true },
    );
    return response.data;
  },

  /** Board #262: copy a platform-wide category into the caller's own list.
   *  A broker admin cannot edit the seeded rows (that would reconfigure every
   *  other tenant); cloning is how they adapt one. */
  async cloneTicketCategory(id: string): Promise<SupportTicketCategory> {
    const response = await api.post<SupportTicketCategory>(
      `/communications/ticket-categories/${id}/clone/`,
      {},
    );
    return response.data;
  },

  async getTicket(id: string): Promise<SupportTicket> {
    const response = await api.get<SupportTicket>(`/communications/tickets/${id}/`);
    return response.data;
  },

  async createTicket(data: SupportTicketCreatePayload): Promise<SupportTicket> {
    const response = await api.post<SupportTicket>('/communications/tickets/', data);
    return response.data;
  },

  async updateTicket(id: string, data: Partial<SupportTicket>): Promise<SupportTicket> {
    const response = await api.patch<SupportTicket>(`/communications/tickets/${id}/`, data);
    return response.data;
  },

  // Chat (board #232)
  //
  // Both calls were aimed at routes that do not exist. `ChatRoomViewSet` is
  // registered at `chat/rooms/` (urls.py), so the message list is
  // `chat/rooms/{pk}/messages/`, not `chat/{id}/messages/` -- and there is no
  // POST on that action at all; sending goes to the `send_message` action.
  // The body was `{message}`; `ChatMessageCreateSerializer` requires
  // `content`, so even against the right URL every send was a 400.
  async getChatRooms(): Promise<ChatRoomRow[]> {
    const response = await api.get<PaginatedResponse<ChatRoomRow>>('/communications/chat/rooms/?page_size=100');
    return response.data.results;
  },

  async getChatMessages(roomId: string): Promise<ChatMessageRow[]> {
    const response = await api.get<ChatMessageRow[]>(`/communications/chat/rooms/${roomId}/messages/`);
    return response.data;
  },

  async sendChatMessage(roomId: string, message: string): Promise<ChatMessageRow> {
    const response = await api.post<ChatMessageRow>(
      `/communications/chat/rooms/${roomId}/send_message/`,
      { content: message, message_type: 'text' }
    );
    return response.data;
  },

  async createChatRoom(name: string, description = ''): Promise<ChatRoomDetail> {
    const response = await api.post<ChatRoomDetail>('/communications/chat/rooms/', {
      name,
      room_type: 'group',
      description,
    });
    return response.data;
  },
};
